import { z } from 'zod';

import { UuidSchema } from '../common/id/id';

import { AiFeatureKeySchema } from './ai-feature-key/ai-feature-key';

/**
 * What every AI request carries, whatever the operation.
 *
 * ## Why there are no sampling knobs here
 *
 * No `temperature`, no `topP`, no `topK`, no thinking configuration, no effort
 * level — and their absence is a decision, not an omission.
 *
 * The model string is **operator configuration**, resolved from the integrations
 * vault at call time (N21), so the platform cannot know which model a request
 * will land on. Across the models an operator may legitimately configure, those
 * knobs are not merely ignored — they are rejected: current Anthropic models
 * return 400 for `temperature`/`top_p`/`top_k`, an explicit thinking-disabled
 * configuration is a 400 on some of them, and the effort parameter errors on
 * exactly the cheap models an operator would pick for high-volume moderation.
 *
 * A knob that turns a working deployment into a 500 the moment staff change a
 * model in a form is worse than no knob at all. So the neutral contract carries
 * none, each **adapter** owns the per-model policy for its own vendor, and an
 * operator tunes cost and depth by choosing the model.
 */
export const AiRequestBaseSchema = z.object({
  /** Who this call is for — the rate-limit window and the usage-log grouping. */
  featureKey: AiFeatureKeySchema,
  /**
   * The member on whose behalf the call is made, or `null` for platform-initiated
   * work (a nightly re-scan, a batch photo check).
   *
   * Required-and-nullable rather than optional on purpose: the per-user rate
   * limit is meaningless if a caller can omit the principal by forgetting a
   * field, so "there is no member here" has to be said out loud.
   */
  userId: UuidSchema.nullable(),
  /**
   * Skips both the cache lookup and the cache store for this call.
   *
   * For features whose answer is deliberately not reproducible. Cacheability is
   * a property of the feature, not of the platform (`03-ai-platform.md` §cache).
   */
  noCache: z.boolean().optional(),
});

export type AiRequestBase = z.infer<typeof AiRequestBaseSchema>;
